import { ArrowLeftOutlined, ArrowRightOutlined, LoadingOutlined } from "@ant-design/icons";
import { Input, Spin, Tooltip } from "antd";
import SaudiRiyal from "assets/svg/saudi_riyal_symbol.svg";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLazyGetSalaryQuery, useUpdateSalaryMutation } from "services/auth";
import { Button } from "shared/components/Button";
import routes, { appRoute } from "shared/constants/routes";
import { isSalaryInRange } from "shared/constants/salary";
import formatDateDDMMYYYY from "utilities/formatDate";
import { useTranslation } from "../../../contexts/TranslationContext";

// Settings › Salary Info.
//
// The salary can only be changed once every 30 days — the backend reports this
// as CAN_UPDATE (1/0) with NEXT_UPDATE_DATE. When it's locked the field stays
// read-only and we explain when it can next be changed.
const SalaryInfo = () => {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const { getUser } = useAuthHook();
  const isRtl = language.dir === "rtl";

  const [fetchSalary, { isFetching }] = useLazyGetSalaryQuery();
  const [updateSalary, { isLoading: isUpdating }] = useUpdateSalaryMutation();

  const [salary, setSalary] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [canUpdate, setCanUpdate] = useState(true);
  const [nextUpdateDate, setNextUpdateDate] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const userId = getUser()?.IDB_USR_ID;

  const loadSalary = async () => {
    if (!userId) return;
    try {
      // { success, result: { SALARY, SALARY_UPDATED_AT, CAN_UPDATE, NEXT_UPDATE_DATE } }
      const res = await fetchSalary({ id: Number(userId) }).unwrap();
      const result = res?.result || {};
      const value = result.SALARY;
      const editable = Number(result.CAN_UPDATE) === 1;

      setCanUpdate(editable);
      setNextUpdateDate(result.NEXT_UPDATE_DATE || null);

      if (value != null) {
        setSalary(String(value));
        // Existing salary: require an explicit "Edit Salary" press, and only
        // when the 30-day window has elapsed.
        setIsEditing(false);
      } else {
        // Nothing on file yet — let the user type it straight in rather than
        // making them press "Edit Salary" on an empty field first.
        setSalary("");
        setIsEditing(editable);
      }
    } catch (err) {
      console.error("Failed to load salary:", err);
      toast.error(translate("SETTINGS.SALARY_LOAD_FAILED"));
    } finally {
      setLoadingInitial(false);
    }
  };

  useEffect(() => {
    loadSalary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const navigateBack = () => {
    navigate(appRoute(routes.SETTING_ROOT));
  };

  // Shown on hover over the disabled "Edit Salary" button while the 30-day
  // change window is still open.
  const lockedNote = [
    translate("SETTINGS.SALARY_LOCKED"),
    nextUpdateDate
      ? `${translate("SETTINGS.SALARY_NEXT_UPDATE")} ${formatDateDDMMYYYY(nextUpdateDate)}.`
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleUpdate = async () => {
    if (!isSalaryInRange(salary)) {
      toast.error(translate("SETTINGS.SALARY_OUT_OF_RANGE"));
      return;
    }

    try {
      await updateSalary({ id: Number(userId), salary: Number(salary) }).unwrap();
      toast.success(translate("SETTINGS.SALARY_UPDATE_SUCCESS"));
      setIsEditing(false);
      // Re-read so the field, the lock state and the next-update date all
      // reflect what the backend now holds.
      loadSalary();
    } catch (err) {
      console.error("Failed to update salary:", err);
      const apiError = err?.data?.error;

      // 403 SALARY_CHANGE_NOT_ALLOWED — the 30-day window hasn't elapsed.
      if (apiError?.errorCode === "SALARY_CHANGE_NOT_ALLOWED") {
        setCanUpdate(false);
        setIsEditing(false);
        if (apiError?.details?.nextUpdateDate) {
          setNextUpdateDate(apiError.details.nextUpdateDate);
        }
        toast.error(translate("SETTINGS.SALARY_LOCKED"));
        return;
      }

      toast.error(
        apiError?.errorMessage ||
        apiError?.message ||
        err?.data?.message ||
        translate("SETTINGS.SALARY_UPDATE_FAILED")
      );
    }
  };

  return (
    <div className="rounded-xl min-h-16 mb-4 px-4 py-8 bg-white items-center shadow">
      <div className="gap-3 flex items-center">
        <div
          className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10 cursor-pointer"
          onClick={navigateBack}
        >
          {isRtl ? (
            <ArrowRightOutlined className="text-black text-lg font-extrabold" />
          ) : (
            <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
          )}
        </div>
        <div className="text-[#161616] text-[22px] font-normal font-sans">
          {translate("SETTINGS.SALARY_INFO")}
        </div>
      </div>

      {loadingInitial ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col sm:w-full lg:w-1/3">
            <label
              htmlFor="salary"
              className="text-[#2A353D] mb-2 text-base font-normal"
            >
              {translate("SETTINGS.CURRENT_SALARY")}
            </label>
            <Input
              id="salary"
              value={salary}
              disabled={!isEditing || isFetching}
              onChange={(e) => setSalary(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder={translate("SETTINGS.CURRENT_SALARY_PLACEHOLDER")}
              size="large"
              inputMode="numeric"
              className="rounded-xl"
              prefix={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
            />
          </div>

          <div className="flex items-center justify-end mt-8">
            {isEditing ? (
              <Button
                color="primary"
                onClick={handleUpdate}
                disabled={isUpdating}
                className="rounded-full h-12 w-48"
              >
                {isUpdating ? <LoadingOutlined /> : translate("SETTINGS.UPDATE_SALARY")}
              </Button>
            ) : canUpdate ? (
              <Button
                color="primary"
                onClick={() => setIsEditing(true)}
                className="rounded-full h-12 w-48"
              >
                {translate("SETTINGS.EDIT_SALARY")}
              </Button>
            ) : (
              // Locked by the 30-day rule. A disabled <button> swallows mouse
              // events, so the tooltip hangs off a wrapper and the button
              // itself ignores pointer events.
              <Tooltip title={lockedNote}>
                <span className="inline-block cursor-not-allowed">
                  <Button
                    color="primary"
                    disabled
                    className="rounded-full h-12 w-48 pointer-events-none opacity-60"
                  >
                    {translate("SETTINGS.EDIT_SALARY")}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryInfo;
